import { localDayOf } from "../../shared/format/local-date.js";
import { createIcons, Plus, Trash2, Edit, FileText, AlertTriangle, Eye, Download, X, CheckCircle2, Search, Filter, Calendar, Building, MapPin, User, RotateCcw } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import type { IntegrityReportId } from "../../shared/kernel/identifiers.js";
import { canDeleteReport } from "../../domain/report/integrity-report-domain.js";
import "../components/document/pdf-preview.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker } from "../context/context-load.js";

class AppIntegrityReportsPage extends HTMLElement {
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Um erro de permissão deixa de
   * virar lista vazia e de produzir "nenhum cadastro" com o cadastro
   * existente na base.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private reportToDelete: string | null = null;
  private previewSnapshot: any | null = null;

  // Data
  private rawReports: any[] = [];
  private clients: any[] = [];
  private unitAreas: any[] = [];
  private professionals: any[] = [];
  private equipments: any[] = [];
  private hasCreatePermission: boolean = false;

  // Filter state
  private searchTerm: string = "";
  private filterClient: string = "";
  private filterUnit: string = "";
  private filterInspector: string = "";
  private filterDateStart: string = "";
  private filterDateEnd: string = "";

  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  connectedCallback() {
    this.filterClient = contextStore.get().activeClientId || "";
    this.loadData();
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  disconnectedCallback() {
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData() {
    this.innerHTML = `
      <style>${CONTEXT_LOAD_STYLES}</style>
      ${this.ctxLoad.banner()}
      <div style="padding: 56px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
        <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
        </svg>
        <span class="text-body-small">Carregando relatórios de integridade...</span>
      </div>
    `;
    
    try {
      this.hasCreatePermission = contextStore.hasPermission(PermissionCode.ReportCreate);
      const ctx = {
        activeClientId: this.filterClient || contextStore.get().activeClientId,
        userId: contextStore.get().userId,
        permissions: contextStore.get().permissions
      } as any;
      const [reports, professionals, clients, unitAreas, equipments] = await Promise.all([
        window.app.platform.reportService.listReports(ctx).catch(() => []),
        await this.ctxLoad.load("Profissionais", () => window.app.platform.adminService.listProfessionals(ctx)),
        await this.ctxLoad.load("Clientes", () => window.app.platform.adminService.listClients(ctx)),
        window.app.platform.projectService ? await this.ctxLoad.load("Unidades / Áreas", () => window.app.platform.projectService.listUnitAreas(undefined, undefined, ctx)) : [],
        await this.ctxLoad.load("Equipamentos", () => window.app.platform.equipmentService.listEquipments(ctx))
      ]);

      this.professionals = Array.from(professionals || []);
      this.clients = Array.from(clients || []);
      this.unitAreas = Array.from(unitAreas || []);
      this.equipments = Array.from(equipments || []);

      this.rawReports = await Promise.all((Array.isArray(reports) ? reports : []).map(async (r: any) => {
         let clientName = r.clientId;
         try {
            const cli = this.clients.find((c: any) => c.id === r.clientId || c.code === r.clientId);
            if (cli) clientName = cli.fantasyName || cli.name || cli.corporateName || clientName;
         } catch {
            /* ignore lookup failure */
         }
         
         let equipmentName = r.equipmentSnapshotId || 'N/A';
         let equipmentUnit = '';
         try {
            if (r.equipmentSnapshotId) {
               const snap = await window.app.platform.equipmentService.getSnapshot(ctx, r.equipmentSnapshotId);
               if (snap) {
                 const eqData = snap.equipmentData || (snap.payloadJson ? JSON.parse(snap.payloadJson) : null);
                 if (eqData) {
                   equipmentName = eqData.tag || eqData.name || equipmentName;
                   equipmentUnit = eqData.unitAreaName || eqData.area || eqData.location || '';
                 }
               }
            }
         } catch {
            /* ignore lookup failure */
         }

         let inspectorName = r.inspectorName || '';
         if (!inspectorName && r.inspectorId) {
           const prof = this.professionals.find((p: any) => p.id === r.inspectorId || p.name === r.inspectorId);
           if (prof && prof.name) inspectorName = prof.name;
           else if (!r.inspectorId.startsWith('PROF-') && !r.inspectorId.startsWith('usr_')) inspectorName = r.inspectorId;
         }
         if (!inspectorName) {
           inspectorName = contextStore.get().userName || 'Desconhecido';
         }
         
         return {
            ...r,
            clientName,
            equipmentName,
            equipmentUnit,
            inspectorName
         };
      }));
      
      this.render();
    } catch (e: any) {
      this.innerHTML = `<div style="padding: 40px; color: var(--status-danger-solid);">Erro ao carregar relatórios: ${e.message}</div>`;
    }
  }

  private getFilteredReports() {
    return this.rawReports.filter(r => {
      const targetClientId = r.clientId || '';
      const clientName = r.clientName || '';
      const inspectorName = r.inspectorName || '';
      const equipmentName = r.equipmentName || '';
      const unitName = r.equipmentUnit || '';
      const reportNumber = r.reportNumber || r.id || '';

      // 1. Text Search
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        const matches = reportNumber.toLowerCase().includes(term) ||
                        equipmentName.toLowerCase().includes(term) ||
                        clientName.toLowerCase().includes(term) ||
                        inspectorName.toLowerCase().includes(term) ||
                        unitName.toLowerCase().includes(term);
        if (!matches) return false;
      }

      // 2. Client Filter
      if (this.filterClient) {
        const matchesClient = targetClientId === this.filterClient ||
                              clientName.toLowerCase() === this.filterClient.toLowerCase();
        if (!matchesClient) return false;
      }

      // 3. Unit Filter
      if (this.filterUnit) {
        const matchesUnit = unitName.toLowerCase() === this.filterUnit.toLowerCase() ||
                            unitName.toLowerCase().includes(this.filterUnit.toLowerCase());
        if (!matchesUnit) return false;
      }

      // 4. Inspector Filter
      if (this.filterInspector) {
        const matchesInspector = r.inspectorId === this.filterInspector ||
                                 inspectorName.toLowerCase() === this.filterInspector.toLowerCase() ||
                                 inspectorName.toLowerCase().includes(this.filterInspector.toLowerCase());
        if (!matchesInspector) return false;
      }

      // 5. Date Filter
      const rawDate = r.createdAt || r.updatedAt;
      if (rawDate) {
        const dateStr = localDayOf(rawDate);
        if (this.filterDateStart && dateStr < this.filterDateStart) return false;
        if (this.filterDateEnd && dateStr > this.filterDateEnd) return false;
      }

      return true;
    });
  }

  private getAvailableClients(): { id: string; name: string }[] {
    const map = new Map<string, string>();
    this.clients.forEach(c => {
      const name = c.fantasyName || c.name || c.corporateName || c.id;
      map.set(c.id, name);
    });
    this.rawReports.forEach(r => {
      if (r.clientId && !map.has(r.clientId)) {
        map.set(r.clientId, r.clientName || r.clientId);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }

  private getAvailableUnits(): { id: string; name: string }[] {
    const map = new Map<string, string>();
    this.unitAreas.forEach(u => {
      if (u.id) {
        const name = u.name || u.code || u.id;
        map.set(name, name);
      }
    });
    this.equipments.forEach(eq => {
      const uName = eq.unitAreaName || eq.area || eq.location;
      if (uName && !map.has(uName)) map.set(uName, uName);
    });
    this.rawReports.forEach(r => {
      if (r.equipmentUnit && !map.has(r.equipmentUnit)) {
        map.set(r.equipmentUnit, r.equipmentUnit);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }

  private getAvailableInspectors(): { id: string; name: string }[] {
    const map = new Map<string, string>();
    this.professionals.forEach(p => {
      if (p.name) map.set(p.name, p.name);
    });
    this.rawReports.forEach(r => {
      if (r.inspectorName && r.inspectorName !== 'Inspetor Responsável' && !map.has(r.inspectorName)) {
        map.set(r.inspectorName, r.inspectorName);
      }
    });
    if (contextStore.get().userName && !map.has(contextStore.get().userName)) {
      map.set(contextStore.get().userName, contextStore.get().userName);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }

  private render() {
    const filteredReports = this.getFilteredReports();
    const availableClients = this.getAvailableClients();
    const availableUnits = this.getAvailableUnits();
    const availableInspectors = this.getAvailableInspectors();
    const hasActiveFilters = Boolean(
      this.searchTerm ||
      this.filterClient ||
      this.filterUnit ||
      this.filterInspector ||
      this.filterDateStart ||
      this.filterDateEnd
    );

    this.innerHTML = `
        <style>
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            flex-wrap: wrap;
            gap: 16px;
          }
          .page-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: var(--color-text-primary);
            letter-spacing: -0.01em;
          }
          .btn-primary {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background: var(--ia-action-bg, var(--color-primary));
            color: var(--ia-action-fg, #F7F8F8); !important;
            border: 1px solid var(--color-text-muted);
            padding: 0.5rem 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            transition: all 0.2s;
          }
          .btn-primary:hover {
            background-color: var(--color-primary);
          }

          /* Filter Bar */
          .filter-bar {
            background: var(--color-graphite-800);
            border: 1px solid var(--color-primary);
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 24px;
            display: flex;
            flex-direction: column;
            gap: 18px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          .filter-row {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            align-items: flex-end;
            width: 100%;
          }
          .filter-group {
            flex: 1 1 200px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .filter-label {
            font-size: 0.75rem;
            font-weight: 700;
            color: var(--color-graphite-400);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .filter-input, .filter-select {
            background: var(--color-primary);
            border: 1px solid var(--color-graphite-700);
            color: var(--ia-fg, #F7F8F8);
            padding: 10px 12px;
            border-radius: 8px;
            font-size: 0.875rem;
            outline: none;
            width: 100%;
            box-sizing: border-box;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .filter-select {
            cursor: pointer;
            appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            background-size: 14px;
            padding-right: 36px;
          }
          .filter-input:focus, .filter-select:focus {
            border-color: var(--color-ink);
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
          }
          .btn-reset-filters {
            background: rgba(239, 68, 68, 0.08);
            border: 1px solid rgba(239, 68, 68, 0.25);
            color: var(--status-danger-border);
            padding: 10px 16px;
            border-radius: 8px;
            font-size: 0.85rem;
            font-weight: 600;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s;
            white-space: nowrap;
          }
          .btn-reset-filters:hover {
            background: rgba(239, 68, 68, 0.15);
            color: var(--color-text-primary);
            border-color: var(--status-danger-solid);
          }

          .reports-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            background: var(--color-graphite-800);
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid var(--color-primary);
          }
          .reports-table th, .reports-table td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid var(--color-primary);
            font-size: 0.875rem;
          }
          .reports-table th {
            background: var(--color-primary);
            font-weight: 700;
            color: var(--color-text-primary); !important;
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.05em;
          }
          .reports-table tr:hover td {
            background: rgba(255, 255, 255, 0.02);
          }
          .reports-table tr:last-child td {
            border-bottom: none;
          }
          .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 700;
          }
          .status-draft { background: var(--ia-warning-bg, #78350f); color: #fef08a !important; border: 1px solid var(--status-warning-text); }
          .status-approved { background: var(--ia-success-bg, #064e3b); color: var(--status-success-border) !important; border: 1px solid var(--status-success-solid); }
          .status-other { background: var(--ia-info-bg, #0c4a6e); color: var(--color-border-subtle) !important; border: 1px solid var(--color-ink); }
          
          .btn-action-sm {
            background: var(--color-primary);
            border: 1px solid var(--color-graphite-700);
            color: var(--color-text-primary); !important;
            padding: 5px 10px;
            border-radius: 5px;
            cursor: pointer;
            font-size: 0.78rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            transition: all 0.2s;
          }
          .btn-action-sm:hover {
            background: var(--color-graphite-700);
          }
          .btn-delete-report {
            background: var(--ia-danger-bg, #450a0a);
            border-color: var(--status-danger-text);
            color: var(--status-danger-border) !important;
          }
          .btn-delete-report:hover {
            background: var(--status-danger-text);
            color: var(--color-text-primary); !important;
          }
          .btn-finalize {
            background: var(--status-success-text);
            border-color: var(--status-success-text);
            color: var(--color-text-primary); !important;
          }
          .btn-finalize:hover {
            background: var(--status-success-text);
          }

          /* Confirm Delete Modal */
          .modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            backdrop-filter: blur(4px);
          }
          .modal-box {
            background: var(--color-graphite-800);
            border: 1px solid var(--color-graphite-700);
            border-radius: 12px;
            padding: 24px;
            max-width: 440px;
            width: 90%;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
            color: var(--color-text-primary);
          }
          .modal-title {
            font-size: 1.15rem;
            font-weight: 700;
            color: var(--status-danger-solid);
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
          }
          .modal-body {
            font-size: 0.9rem;
            color: var(--color-graphite-300);
            line-height: 1.5;
            margin-bottom: 20px;
          }
          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
          }

          /* Fullscreen PDF Modal */
          .pdf-modal-container {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.85);
            display: none;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            padding: 20px;
          }
          .pdf-modal-content {
            background: var(--color-graphite-800);
            border: 1px solid var(--color-graphite-700);
            border-radius: 12px;
            width: 100%;
            max-width: 1100px;
            height: 92vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          }
          .pdf-modal-header {
            padding: 12px 18px;
            background: var(--color-primary);
            border-bottom: 1px solid var(--color-graphite-700);
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: var(--color-text-primary);
          }
        </style>
        
        <div class="page-header">
          <div>
            <div class="page-title">Gestão de Relatórios de Integridade</div>
            <div style="font-size: 0.85rem; color: var(--color-graphite-400); margin-top: 4px;">Gerenciamento de Laudos Técnicos NR-13 e Registros Oficiais</div>
          </div>
          ${this.hasCreatePermission ? `
            <button class="btn-primary" id="btn-new-report">
              <i data-lucide="plus" style="width: 18px; height: 18px;"></i> Nova Inspeção (Gerar RI)
            </button>
          ` : ''}
        </div>

        <!-- Filter Bar -->
        <div class="filter-bar">
          <div class="filter-row">
            <!-- Buscar -->
            <div class="filter-group" style="grid-column: span 1;">
              <label class="filter-label"><i data-lucide="search" style="width: 12px; height: 12px;"></i> Buscar Laudo / TAG</label>
              <input type="text" class="filter-input" id="inp-search-term" placeholder="Nº RI, TAG ou Equipamento..." value="${this.escapeHtml(this.searchTerm)}">
            </div>

            <!-- Cliente -->
            <div class="filter-group">
              <label class="filter-label"><i data-lucide="building" style="width: 12px; height: 12px;"></i> Cliente</label>
              <select class="filter-select" id="sel-filter-client">
                <option value="">Todos os Clientes</option>
                ${availableClients.map(c => `
                  <option value="${c.id}" ${this.filterClient === c.id ? 'selected' : ''}>${c.name}</option>
                `).join('')}
              </select>
            </div>

            <!-- Unidade / Área -->
            <div class="filter-group">
              <label class="filter-label"><i data-lucide="map-pin" style="width: 12px; height: 12px;"></i> Unidade / Área</label>
              <select class="filter-select" id="sel-filter-unit">
                <option value="">Todas as Unidades</option>
                ${availableUnits.map(u => `
                  <option value="${u.id}" ${this.filterUnit === u.id ? 'selected' : ''}>${u.name}</option>
                `).join('')}
              </select>
            </div>

            <!-- Inspetor -->
            <div class="filter-group">
              <label class="filter-label"><i data-lucide="user" style="width: 12px; height: 12px;"></i> Inspetor</label>
              <select class="filter-select" id="sel-filter-inspector">
                <option value="">Todos os Inspetores</option>
                ${availableInspectors.map(p => `
                  <option value="${p.id}" ${this.filterInspector === p.id ? 'selected' : ''}>${p.name}</option>
                `).join('')}
              </select>
            </div>

            <!-- Data De -->
            <div class="filter-group">
              <label class="filter-label"><i data-lucide="calendar" style="width: 12px; height: 12px;"></i> Data De</label>
              <input type="date" class="filter-input" id="inp-filter-date-start" value="${this.filterDateStart}">
            </div>

            <!-- Data Até -->
            <div class="filter-group">
              <label class="filter-label"><i data-lucide="calendar" style="width: 12px; height: 12px;"></i> Data Até</label>
              <input type="date" class="filter-input" id="inp-filter-date-end" value="${this.filterDateEnd}">
            </div>

            <!-- Botão Limpar -->
            ${hasActiveFilters ? `
              <div class="filter-group" style="align-self: flex-end;">
                <button type="button" class="btn-reset-filters" id="btn-clear-filters">
                  <i data-lucide="rotate-ccw" style="width: 14px; height: 14px;"></i> Limpar Filtros
                </button>
              </div>
            ` : ''}
          </div>
        </div>

        <div id="reports-list">
          ${filteredReports.length === 0 ? `
            <div style="background: var(--color-graphite-800); border: 1px dashed var(--color-graphite-700); border-radius: 8px; padding: 40px; text-align: center; color: var(--color-graphite-400);">
              <i data-lucide="file-text" style="width: 36px; height: 36px; margin-bottom: 12px; color: var(--color-text-secondary);"></i>
              <div style="font-size: 1.1rem; font-weight: 600; color: var(--color-text-primary); margin-bottom: 6px;">Nenhum relatório encontrado</div>
              <div style="font-size: 0.875rem;">${hasActiveFilters ? 'Nenhum relatório corresponde aos critérios de filtro selecionados.' : 'Não há relatórios de integridade cadastrados.'}</div>
              ${hasActiveFilters ? `
                <button type="button" class="btn-reset-filters" id="btn-clear-filters-empty" style="margin-top: 16px;">
                  <i data-lucide="rotate-ccw" style="width: 14px; height: 14px;"></i> Redefinir Filtros
                </button>
              ` : ''}
            </div>
          ` : `
            <div style="overflow-x: auto;">
              <table class="reports-table">
                <thead>
                  <tr>
                    <th>Relatório</th>
                    <th>TAG</th>
                    <th>Cliente</th>
                    <th>Inspetor</th>
                    <th>Data</th>
                    <th>Pendências</th>
                    <th>Revisão</th>
                    <th>Status</th>
                    <th style="text-align: right;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  ${filteredReports.map(r => `
                    <tr>
                      <td style="font-weight: 700; color: var(--color-text-primary);">${r.reportNumber || r.id}</td>
                      <td><code style="color: var(--color-text-muted); font-weight: 700;">${r.equipmentName}</code></td>
                      <td style="color: var(--color-graphite-300);">${r.clientName}</td>
                      <td style="color: var(--color-bg-secondary); font-weight: 500;">${r.inspectorName || r.inspectorId || 'Inspetor'}</td>
                      <td style="color: var(--color-graphite-300);">${new Date(r.createdAt || Date.now()).toLocaleDateString('pt-BR')}</td>
                      <td><span style="color: var(--status-danger-border);">0 Impeditivas</span></td>
                      <td style="color: var(--color-graphite-300);">Rev. ${r.version || '00'}</td>
                      <td>
                        <span class="status-badge ${r.status === 'DRAFT' || r.status === 'IN_ELABORATION' ? 'status-draft' : (r.status === 'ENGINEERING_APPROVED' || r.status === 'APPROVED' ? 'status-approved' : 'status-other')}">
                          ${r.status === 'DRAFT' || r.status === 'IN_ELABORATION' ? 'EM ELABORAÇÃO' : r.status}
                        </span>
                      </td>
                      <td style="text-align: right; white-space: nowrap;">
                        <button type="button" class="btn-action-sm btn-edit-report" data-report-id="${r.id}" title="Continuar Inspeção/Checklist">
                          <i data-lucide="edit" style="width:14px; height:14px; color: var(--color-text-muted);"></i> Checklist
                        </button>
                        ${canDeleteReport(r, { permissions: contextStore.get().permissions, professionalId: contextStore.get().professionalId }) ? `
                          <button type="button" class="btn-action-sm btn-delete-report" data-report-id="${r.id}" title="${(r.status === 'DRAFT' || r.status === 'IN_ELABORATION') ? 'Excluir Relatório' : 'Exclusão Administrativa'}">
                            <i data-lucide="trash-2" style="width:14px; height:14px; color: var(--status-danger-solid);"></i> Excluir
                          </button>
                        ` : ''}
                        ${r.status === 'DRAFT' || r.status === 'IN_ELABORATION' ? `
                          <button type="button" class="btn-action-sm btn-finalize btn-finalize-report" data-report-id="${r.id}">
                            <i data-lucide="check-circle-2" style="width:14px; height:14px;"></i> Finalizar
                          </button>
                        ` : `
                          <button type="button" class="btn-action-sm btn-view-fullscreen" data-report-id="${r.id}">
                            <i data-lucide="eye" style="width:14px; height:14px; color: var(--color-text-muted);"></i> Visualizar
                          </button>
                        `}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `}
        </div>

        <div id="confirm-modal-container"></div>

        <!-- PDF Preview Modal -->
        <div id="pdf-view-modal" class="pdf-modal-container">
          <div class="pdf-modal-content">
            <div class="pdf-modal-header">
              <div style="font-weight: 600; display: flex; align-items: center; gap: 8px;">
                <i data-lucide="file-text" style="width: 18px; height: 18px; color: var(--color-text-muted);"></i>
                <span>Visualizador Oficial de Laudo de Integridade NR-13</span>
              </div>
              <button type="button" id="btn-close-pdf-preview" style="background: transparent; border: none; color: var(--color-graphite-400); cursor: pointer; padding: 4px; display: flex; align-items: center;">
                <i data-lucide="x" style="width: 20px; height: 20px;"></i>
              </button>
            </div>
            <div style="flex: 1; position: relative; overflow: hidden;">
              <app-pdf-preview id="list-pdf-preview" style="height: 100%; display: block;"></app-pdf-preview>
            </div>
          </div>
        </div>
      `;

      createIcons({
        icons: { Plus, Trash2, Edit, FileText, AlertTriangle, Eye, Download, X, CheckCircle2, Search, Filter, Calendar, Building, MapPin, User, RotateCcw },
        nameAttr: "data-lucide",
        root: this
      });

      // Wire Filter Events
      this.querySelector('#inp-search-term')?.addEventListener('input', (e) => {
        this.searchTerm = (e.target as HTMLInputElement).value;
        this.render();
      });

      this.querySelector('#sel-filter-client')?.addEventListener('change', (e) => {
        const clientVal = (e.target as HTMLSelectElement).value;
        this.filterClient = clientVal;
        contextStore.set({ activeClientId: clientVal || null });
        this.render();
      });

      this.querySelector('#sel-filter-unit')?.addEventListener('change', (e) => {
        this.filterUnit = (e.target as HTMLSelectElement).value;
        this.render();
      });

      this.querySelector('#sel-filter-inspector')?.addEventListener('change', (e) => {
        this.filterInspector = (e.target as HTMLSelectElement).value;
        this.render();
      });

      this.querySelector('#inp-filter-date-start')?.addEventListener('change', (e) => {
        this.filterDateStart = (e.target as HTMLInputElement).value;
        this.render();
      });

      this.querySelector('#inp-filter-date-end')?.addEventListener('change', (e) => {
        this.filterDateEnd = (e.target as HTMLInputElement).value;
        this.render();
      });

      const clearFilters = () => {
        this.searchTerm = "";
        this.filterClient = "";
        this.filterUnit = "";
        this.filterInspector = "";
        this.filterDateStart = "";
        this.filterDateEnd = "";
        this.render();
      };

      this.querySelector('#btn-clear-filters')?.addEventListener('click', clearFilters);
      this.querySelector('#btn-clear-filters-empty')?.addEventListener('click', clearFilters);

      const btnNew = this.querySelector('#btn-new-report');
      if (btnNew) {
        btnNew.addEventListener('click', () => {
          router.navigate('/inspections/new');
        });
      }

      this.querySelectorAll('.btn-view-fullscreen').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const reportId = (e.currentTarget as HTMLElement).dataset['reportId'];
          if (reportId) {
            router.navigate(`/reports/integrity/view/${reportId}`);
          }
        });
      });

      this.querySelectorAll('.btn-edit-report').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const reportId = (e.currentTarget as HTMLElement).dataset['reportId'];
          if (reportId) {
            router.navigate(`/reports/integrity/checklist/${reportId}`);
          }
        });
      });

      this.querySelectorAll('.btn-finalize-report').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const reportId = (e.currentTarget as HTMLElement).dataset['reportId'];
          if (reportId) {
            try {
              const result = await window.app.platform.reportService.validateIntegrityReport(sessionRequestContext(), reportId as any);
              if (result.hasImpeditive) {
                alert('O relatório possui pendências IMPEDITIVAS que impedem a finalização. Resolva-as antes de prosseguir:\n\n' + result.errors.filter(e => e.type === 'IMPEDITIVE').map(e => '- ' + e.message).join('\n'));
              } else {
                const conf = confirm('Deseja realmente finalizar o Relatório de Integridade? Ele passará para validação final.');
                if (conf) {
                   const report = await window.app.platform.reportService.getReportById(sessionRequestContext(), reportId as any);
                   if (report) {
                     report.status = 'FINALIZED_BY_INSPECTOR';
                     await window.app.platform.reportService.updateReport(sessionRequestContext(), report);
                     this.loadData();
                   }
                }
              }
            } catch (err: any) {
              alert('Erro ao validar relatório: ' + err.message);
            }
          }
        });
      });

      this.querySelectorAll('.btn-delete-report').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const reportId = (e.currentTarget as HTMLElement).dataset['reportId'];
          if (reportId) {
            this.showDeleteConfirmation(reportId);
          }
        });
      });

      this.querySelector('#btn-close-pdf-preview')?.addEventListener('click', () => {
        const modal = this.querySelector('#pdf-view-modal') as HTMLElement;
        if (modal) modal.style.display = 'none';
      });
  }

  private escapeHtml(str: string): string {
    return (str || '')
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private showDeleteConfirmation(reportId: string) {
    this.reportToDelete = reportId;
    const modalContainer = this.querySelector('#confirm-modal-container');
    if (!modalContainer) return;

    const targetReport = this.rawReports.find(r => r.id === reportId);
    const isAdvancedStatus = targetReport && targetReport.status !== 'DRAFT' && targetReport.status !== 'IN_ELABORATION';

    modalContainer.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal-box">
          <div class="modal-title">
            <i data-lucide="alert-triangle" style="width:20px; height:20px;"></i> ${isAdvancedStatus ? 'Exclusão Administrativa de Relatório' : 'Excluir Relatório'}
          </div>
          <div class="modal-body">
            ${isAdvancedStatus ? `
              O relatório <strong>${reportId}</strong> (Status: <strong>${targetReport.status}</strong>) já entrou no fluxo documental.
              <br><br>
              Esta ação removerá a entidade documental e suas recomendações vinculadas, mas <strong>preservará intactos a Inspeção e o Checklist</strong>.
              <br><br>
              <span style="color:var(--status-danger-solid); font-size:0.825rem;">Confirma a exclusão administrativa permanente deste relatório?</span>
            ` : `
              Tem certeza de que deseja excluir permanentemente o relatório <strong>${reportId}</strong>?
              <br><br>
              <span style="color:var(--status-danger-solid); font-size:0.825rem;">Esta ação removerá todas as constatações, seções e recomendações vinculadas a este relatório e não poderá ser desfeita. A Inspeção e o Checklist continuarão preservados.</span>
            `}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-action-sm" id="btn-cancel-delete" style="padding: 8px 16px;">Cancelar</button>
            <button type="button" class="btn-action-sm btn-delete-report" id="btn-confirm-delete" style="padding: 8px 16px;">
              <i data-lucide="trash-2" style="width:14px; height:14px;"></i> ${isAdvancedStatus ? 'Confirmar Exclusão Administrativa' : 'Confirmar Exclusão'}
            </button>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { AlertTriangle, Trash2 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeDeleteModal = () => {
      modalContainer.innerHTML = '';
      this.reportToDelete = null;
    };

    modalContainer.querySelector('#btn-cancel-delete')?.addEventListener('click', closeDeleteModal);

    modalContainer.querySelector('.modal-backdrop')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        closeDeleteModal();
      }
    });

    modalContainer.querySelector('#btn-confirm-delete')?.addEventListener('click', async () => {
      if (this.reportToDelete) {
        try {
          const ctx = sessionRequestContext();
          await window.app.platform.reportService.deleteReport(ctx, this.reportToDelete as IntegrityReportId);

          modalContainer.innerHTML = '';
          this.reportToDelete = null;
          await this.loadData();
        } catch (err: any) {
          alert(err?.safeMessage || err?.message || 'Não foi possível excluir o relatório.');
        }
      }
    });
  }
}

if (!customElements.get("app-integrity-reports-page")) {
  customElements.define("app-integrity-reports-page", AppIntegrityReportsPage);
}



